import { Smartphone, Monitor, BarChart3, Users, CheckCircle, Calendar } from "lucide-react";

export function LandingShowcase() {
  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden bg-gradient-to-b from-muted/20 to-background">
      {/* Background decoration */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full bg-raven-electric/10 blur-3xl" />
      
      <div className="container mx-auto max-w-7xl relative z-10">
        <div className="text-center mb-16 animate-fade-in">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            Interfaz <span className="landing-gradient-text">Intuitiva</span> y Potente
          </h2>
          <p className="text-lg text-muted-foreground">
            Diseñada para ser fácil de usar pero con todas las funcionalidades que necesitas
          </p>
        </div>

        {/* Desktop View */}
        <div className="mb-20">
          <div className="flex items-center justify-center gap-3 mb-8">
            <Monitor className="w-6 h-6 text-raven-electric" />
            <h3 className="text-2xl font-bold">Panel de Administración</h3>
          </div>
          <div 
            className="landing-glass-card p-6 lg:p-8 animate-fade-in group"
            style={{animationDelay: '0.2s'}}
          >
            {/* Browser Chrome */}
            <div className="flex items-center gap-2 mb-6 pb-4 border-b border-white/10">
              <div className="w-3 h-3 rounded-full bg-red-500/80" />
              <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
              <div className="w-3 h-3 rounded-full bg-green-500/80" />
              <div className="ml-4 text-xs text-muted-foreground font-medium">dashboard.raven.app</div>
            </div>

            {/* Dashboard Mockup */}
            <div className="space-y-6">
              {/* Stats Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { icon: Users, label: "Clientes", value: "248", color: "from-blue-500 to-cyan-500" },
                  { icon: Calendar, label: "Órdenes", value: "156", color: "from-purple-500 to-pink-500" },
                  { icon: CheckCircle, label: "Completadas", value: "89%", color: "from-green-500 to-emerald-500" },
                  { icon: BarChart3, label: "Facturación", value: "€45k", color: "from-orange-500 to-red-500" }
                ].map((stat, i) => (
                  <div 
                    key={i}
                    className="relative overflow-hidden rounded-xl bg-gradient-to-br from-card/80 to-card/40 border border-white/10 p-4 group-hover:scale-105 transition-transform duration-300"
                    style={{transitionDelay: `${i * 0.05}s`}}
                  >
                    <div className={`inline-flex p-2 rounded-lg bg-gradient-to-r ${stat.color} bg-opacity-20 mb-3`}>
                      <stat.icon className="w-4 h-4 text-white" />
                    </div>
                    <div className="text-xs text-muted-foreground mb-1">{stat.label}</div>
                    <div className="text-2xl font-bold">{stat.value}</div>
                  </div>
                ))}
              </div>

              {/* Chart Placeholder */}
              <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-card/80 to-card/40 border border-white/10 p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <div className="text-sm text-muted-foreground mb-1">Órdenes de Trabajo</div>
                    <div className="text-2xl font-bold">Últimos 30 días</div>
                  </div>
                  <div className="flex gap-2">
                    <div className="px-3 py-1 rounded-full bg-raven-electric/20 text-xs text-raven-electric">Semana</div>
                    <div className="px-3 py-1 rounded-full bg-white/5 text-xs text-muted-foreground">Mes</div>
                  </div>
                </div>
                {/* Mock Chart */}
                <div className="flex items-end justify-between gap-2 h-32">
                  {[40, 65, 45, 80, 55, 90, 70, 85, 60, 75, 95, 70].map((height, i) => (
                    <div 
                      key={i}
                      className="flex-1 bg-gradient-to-t from-raven-electric to-raven-violet rounded-t-lg opacity-80 hover:opacity-100 transition-opacity cursor-pointer"
                      style={{height: `${height}%`}}
                    />
                  ))}
                </div>
              </div>

              {/* Table Preview */}
              <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-card/80 to-card/40 border border-white/10 p-6">
                <div className="text-sm text-muted-foreground mb-4">Órdenes Recientes</div>
                <div className="space-y-3">
                  {[
                    { client: "Empresa ABC S.L.", status: "En Progreso", tech: "Juan García" },
                    { client: "Seguridad Total", status: "Completada", tech: "María López" },
                    { client: "Sistemas Pro", status: "Pendiente", tech: "Carlos Ruiz" }
                  ].map((order, i) => (
                    <div 
                      key={i}
                      className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                    >
                      <div className="flex-1">
                        <div className="font-medium">{order.client}</div>
                        <div className="text-xs text-muted-foreground">{order.tech}</div>
                      </div>
                      <div className={`px-3 py-1 rounded-full text-xs ${
                        order.status === "Completada" ? "bg-green-500/20 text-green-400" :
                        order.status === "En Progreso" ? "bg-blue-500/20 text-blue-400" :
                        "bg-yellow-500/20 text-yellow-400"
                      }`}>
                        {order.status}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile View */}
        <div>
          <div className="flex items-center justify-center gap-3 mb-8">
            <Smartphone className="w-6 h-6 text-raven-violet" />
            <h3 className="text-2xl font-bold">App Móvil para Técnicos</h3>
          </div>
          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* Mobile Mockup */}
            <div 
              className="flex justify-center animate-fade-in"
              style={{animationDelay: '0.3s'}}
            >
              <div className="relative w-full max-w-[320px]">
                {/* Phone Frame */}
                <div className="relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-[3rem] p-3 shadow-2xl border-8 border-gray-900">
                  {/* Notch */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-40 h-7 bg-gray-900 rounded-b-3xl z-10" />
                  
                  {/* Screen */}
                  <div className="relative bg-gradient-to-b from-[#0a0f1e] to-[#050810] rounded-[2.5rem] overflow-hidden aspect-[9/19.5]">
                    {/* Status Bar */}
                    <div className="flex items-center justify-between px-6 py-3 text-xs text-white/80">
                      <div>9:41</div>
                      <div className="flex gap-1">
                        <div className="w-4 h-4">📶</div>
                        <div className="w-4 h-4">📡</div>
                        <div className="w-4 h-4">🔋</div>
                      </div>
                    </div>

                    {/* Header */}
                    <div className="px-6 py-4 border-b border-white/10">
                      <div className="text-white text-2xl font-bold mb-1">Orden de Trabajo</div>
                      <div className="text-white/60 text-sm">Cliente: Seguridad XYZ</div>
                    </div>

                    {/* Checklist */}
                    <div className="px-6 py-4 space-y-3">
                      {[
                        { text: "Inspección de Cámaras", done: true },
                        { text: "Verificar Sensores", done: true },
                        { text: "Prueba de Alarmas", done: false },
                        { text: "Registro Fotográfico", done: false },
                        { text: "Firma del Cliente", done: false }
                      ].map((item, i) => (
                        <div 
                          key={i}
                          className="flex items-center gap-3 p-3 rounded-lg bg-white/5"
                        >
                          <div className={`w-5 h-5 rounded-md flex items-center justify-center ${
                            item.done ? 'bg-green-500' : 'bg-white/10 border-2 border-white/20'
                          }`}>
                            {item.done && <CheckCircle className="w-3 h-3 text-white" />}
                          </div>
                          <div className={`text-sm flex-1 ${item.done ? 'text-white/60 line-through' : 'text-white'}`}>
                            {item.text}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Bottom Actions */}
                    <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-[#050810] to-transparent">
                      <button className="w-full py-4 rounded-xl bg-gradient-to-r from-raven-electric to-raven-violet text-white font-bold shadow-lg">
                        Completar Orden
                      </button>
                      <div className="mt-3 flex items-center justify-center gap-2 text-xs text-green-400">
                        <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                        Sincronizado
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Features List */}
            <div 
              className="space-y-6 animate-fade-in"
              style={{animationDelay: '0.4s'}}
            >
              <div>
                <h4 className="font-bold text-xl mb-2 text-foreground">
                  Trabajo en Campo Simplificado
                </h4>
                <p className="text-muted-foreground">
                  Los técnicos completan checklists digitales, capturan fotos y firmas, todo desde su móvil sin conexión a internet.
                </p>
              </div>
              
              <div className="space-y-3">
                {[
                  "Checklists de seguridad interactivos",
                  "Captura de fotos con geolocalización",
                  "Firma digital del cliente",
                  "Sincronización automática",
                  "Notificaciones push en tiempo real"
                ].map((feature, index) => (
                  <div key={index} className="flex items-start gap-3 group">
                    <div className="w-2 h-2 rounded-full bg-gradient-to-r from-raven-violet to-raven-electric mt-2 group-hover:scale-150 transition-transform" />
                    <p className="text-foreground">{feature}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
